<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import {
  buildSoulCloserDraft,
  buildSoulCloserSettingsPayload,
  isSoulCloserEnabled,
} from '../helpers/soulCloserSettings';

const props = defineProps({
  integration: { type: Object, default: null },
  legacyPersonality: { type: String, default: '' },
});

const emit = defineEmits(['saved']);
const { t } = useI18n();
const { currentAccount } = useAccount();
const draft = ref({});
const originalDraft = ref('');
const saving = ref(false);
const prefilledFromGeneralPersonality = ref(false);

const enabled = computed(() =>
  isSoulCloserEnabled(draft.value.soul_closer_enabled)
);
const hasChanges = computed(
  () => JSON.stringify(draft.value) !== originalDraft.value
);
const fields = computed(() =>
  (props.integration?.fields || []).filter(
    field => field.key !== 'soul_closer_enabled'
  )
);
const fieldGroups = computed(() => {
  const visibleFields = fields.value.filter(
    field =>
      field.type !== 'pipeline_stage' ||
      Boolean(draft.value.soul_closer_sales_pipeline_id)
  );
  const groups = [
    {
      key: 'routing',
      number: '01',
      title: t('LUMERAI_SETTINGS.SOULCLOSER.SECTIONS.ROUTING_TITLE'),
      description: t(
        'LUMERAI_SETTINGS.SOULCLOSER.SECTIONS.ROUTING_DESCRIPTION'
      ),
      fields: visibleFields.filter(field =>
        ['inbox', 'calendar', 'team', 'pipeline'].includes(field.type)
      ),
    },
    {
      key: 'rules',
      number: '02',
      title: t('LUMERAI_SETTINGS.SOULCLOSER.SECTIONS.RULES_TITLE'),
      description: t('LUMERAI_SETTINGS.SOULCLOSER.SECTIONS.RULES_DESCRIPTION'),
      fields: visibleFields.filter(
        field =>
          field.type === 'number' ||
          field.type === 'pipeline_stage' ||
          field.key === 'soul_closer_min_tenure_enabled'
      ),
    },
    {
      key: 'personality',
      number: '03',
      title: t('LUMERAI_SETTINGS.SOULCLOSER.SECTIONS.PERSONALITY_TITLE'),
      description: t(
        'LUMERAI_SETTINGS.SOULCLOSER.SECTIONS.PERSONALITY_DESCRIPTION'
      ),
      fields: visibleFields.filter(
        field => field.key === 'soul_closer_personality'
      ),
    },
    {
      key: 'messages',
      number: '04',
      title: t('LUMERAI_SETTINGS.SOULCLOSER.SECTIONS.MESSAGES_TITLE'),
      description: t(
        'LUMERAI_SETTINGS.SOULCLOSER.SECTIONS.MESSAGES_DESCRIPTION'
      ),
      fields: visibleFields.filter(
        field =>
          field.type === 'textarea' && field.key !== 'soul_closer_personality'
      ),
    },
  ];
  const assignedKeys = new Set(
    groups.flatMap(group => group.fields.map(field => field.key))
  );
  const otherFields = visibleFields.filter(
    field => !assignedKeys.has(field.key)
  );
  if (otherFields.length) {
    groups.push({
      key: 'other',
      number: '05',
      title: t('LUMERAI_SETTINGS.SOULCLOSER.SECTIONS.OTHER_TITLE'),
      description: '',
      fields: otherFields,
    });
  }
  return groups.filter(group => group.fields.length);
});
const selectedPipelineStages = computed(() => {
  const pipelineId = String(draft.value.soul_closer_sales_pipeline_id || '');
  return (
    props.integration?.pipelines?.find(
      pipeline => String(pipeline.id) === pipelineId
    )?.stages || []
  );
});

const translationOrFallback = (key, fallback) => {
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  const translated = t(key);
  return translated === key ? fallback : translated;
};
const fieldLabel = field =>
  translationOrFallback(
    `INTEGRATION_SETTINGS.NATIVE.FIELDS.${field.key}`,
    field.label
  );
const fieldDescription = field =>
  translationOrFallback(
    `INTEGRATION_SETTINGS.NATIVE.FIELD_DESCRIPTIONS.${field.key}`,
    field.description
  );

const initializeDraft = integration => {
  if (!integration) return;

  const { values, prefilledFromGeneralPersonality: prefilled } =
    buildSoulCloserDraft(integration, props.legacyPersonality);
  prefilledFromGeneralPersonality.value = prefilled;
  draft.value = values;
  originalDraft.value = JSON.stringify(values);
};

watch(
  () => [props.integration, props.legacyPersonality],
  ([integration]) => initializeDraft(integration),
  { immediate: true }
);

const changePipeline = () => {
  [
    'soul_closer_stage_qualified_id',
    'soul_closer_stage_active_id',
    'soul_closer_stage_exit_limit_id',
    'soul_closer_stage_min_tenure_id',
    'soul_closer_stage_not_interested_id',
  ].forEach(key => {
    draft.value[key] = '';
  });
};

const setEnabled = value => {
  draft.value.soul_closer_enabled = value;
};

const save = async () => {
  if (!props.integration || !currentAccount.value?.id || saving.value) return;

  const previouslyEnabled = enabled.value;
  const settings = buildSoulCloserSettingsPayload(
    props.integration.fields,
    draft.value
  );
  saving.value = true;
  try {
    const { data } = await window.axios.patch(
      `/api/v1/accounts/${currentAccount.value.id}/native_integrations/soul_closer`,
      { settings }
    );
    emit('saved', data);
    initializeDraft(data);
    useAlert(t('LUMERAI_SETTINGS.SOULCLOSER.SAVED'));
  } catch (error) {
    if (!previouslyEnabled) draft.value.soul_closer_enabled = false;
    useAlert(
      error.response?.data?.error || t('LUMERAI_SETTINGS.SOULCLOSER.SAVE_ERROR')
    );
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-5 pb-2">
    <section
      class="relative isolate overflow-hidden rounded-2xl border border-n-brand/20 bg-gradient-to-br from-n-brand/10 via-n-solid-2 to-n-solid-2 p-5 shadow-sm sm:p-7"
    >
      <div
        class="pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-n-brand/10 blur-3xl"
        aria-hidden="true"
      />
      <div
        class="relative flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between"
      >
        <div class="flex min-w-0 items-start gap-4">
          <div
            class="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-n-brand text-white shadow-lg shadow-n-brand/20"
          >
            <fluent-icon icon="bot" size="24" />
          </div>
          <div class="min-w-0">
            <span
              class="mb-2 inline-flex items-center gap-1.5 rounded-full border border-n-brand/20 bg-n-brand/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-n-brand"
            >
              <span class="h-1.5 w-1.5 rounded-full bg-n-brand" />
              {{ t('LUMERAI_SETTINGS.SOULCLOSER.NATIVE_LABEL') }}
            </span>
            <h2 class="m-0 text-xl font-bold tracking-tight text-n-slate-12">
              {{ t('LUMERAI_SETTINGS.SOULCLOSER.TITLE') }}
            </h2>
            <p class="mb-0 mt-1 max-w-3xl text-sm leading-6 text-n-slate-11">
              {{ t('LUMERAI_SETTINGS.SOULCLOSER.DESCRIPTION') }}
            </p>
          </div>
        </div>

        <div
          class="flex shrink-0 flex-col gap-2 rounded-xl border border-n-weak bg-n-solid-1/70 p-4 shadow-sm sm:min-w-64"
        >
          <label
            class="flex items-center justify-between gap-5 text-sm font-semibold text-n-slate-12"
          >
            <span>{{ t('LUMERAI_SETTINGS.SOULCLOSER.ACTIVATE') }}</span>
            <Switch
              :model-value="enabled"
              :aria-label="t('INTEGRATION_SETTINGS.NATIVE.TOGGLE_SOUL_CLOSER')"
              @update:model-value="setEnabled"
            />
          </label>
          <div
            class="flex items-start gap-2 border-t border-n-weak pt-2 text-xs leading-5"
            :class="enabled ? 'text-n-teal-11' : 'text-n-slate-10'"
          >
            <span
              class="mt-1 h-2 w-2 shrink-0 rounded-full"
              :class="enabled ? 'bg-n-teal-9' : 'bg-n-slate-7'"
            />
            <span>
              {{
                enabled
                  ? t('LUMERAI_SETTINGS.SOULCLOSER.ENABLED')
                  : t('LUMERAI_SETTINGS.SOULCLOSER.DISABLED')
              }}
            </span>
          </div>
        </div>
      </div>
    </section>

    <p
      v-if="prefilledFromGeneralPersonality"
      class="m-0 flex items-start gap-3 rounded-xl border border-n-amber-8/30 bg-n-amber-3/20 px-4 py-3.5 text-sm leading-6 text-n-slate-11"
    >
      <fluent-icon
        icon="info"
        size="18"
        class="mt-0.5 shrink-0 text-n-amber-11"
      />
      {{ t('LUMERAI_SETTINGS.SOULCLOSER.LEGACY_PERSONALITY_NOTE') }}
    </p>

    <section
      v-for="group in fieldGroups"
      :key="group.key"
      class="rounded-2xl border border-n-weak bg-n-solid-2 p-5 shadow-sm sm:p-6"
    >
      <header
        class="mb-5 flex items-start gap-3 border-b border-n-weak pb-4 sm:items-center sm:gap-4"
      >
        <span
          class="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-n-brand/10 text-sm font-bold text-n-brand ring-1 ring-inset ring-n-brand/20"
        >
          {{ group.number }}
        </span>
        <div class="min-w-0">
          <h3 class="m-0 text-base font-semibold text-n-slate-12">
            {{ group.title }}
          </h3>
          <p
            v-if="group.description"
            class="mb-0 mt-1 text-sm leading-5 text-n-slate-11"
          >
            {{ group.description }}
          </p>
        </div>
      </header>
      <div class="grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2">
        <label
          v-for="field in group.fields"
          :key="field.key"
          class="block min-w-0 text-sm font-medium text-n-slate-12"
          :class="[
            field.type === 'boolean' ? 'md:col-span-2' : '',
            field.key === 'soul_closer_personality' ? 'md:col-span-2' : '',
          ]"
        >
          <span
            v-if="field.type === 'boolean'"
            class="flex min-h-16 items-center justify-between gap-4 rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3 transition-colors hover:border-n-slate-6"
          >
            <span class="flex min-w-0 flex-col">
              <span class="font-semibold">{{ fieldLabel(field) }}</span>
              <span
                v-if="fieldDescription(field)"
                class="mt-1 text-xs leading-5 text-n-slate-10"
              >
                {{ fieldDescription(field) }}
              </span>
            </span>
            <Switch
              :model-value="Boolean(draft[field.key])"
              :aria-label="fieldLabel(field)"
              @update:model-value="draft[field.key] = $event"
            />
          </span>

          <template v-else-if="field.type === 'team'">
            <span class="mb-2 block">{{ fieldLabel(field) }}</span>
            <select
              v-model="draft[field.key]"
              class="h-11 w-full rounded-xl border border-n-weak bg-n-solid-1 px-3.5 text-sm text-n-slate-12 shadow-sm outline-none transition hover:border-n-slate-6 focus:border-n-brand focus:ring-2 focus:ring-n-brand/20"
            >
              <option value="">
                {{ t('INTEGRATION_SETTINGS.NATIVE.SELECT_TEAM') }}
              </option>
              <option
                v-for="team in integration.teams"
                :key="team.id"
                :value="String(team.id)"
              >
                {{ team.name }}
              </option>
            </select>
          </template>

          <template v-else-if="field.type === 'inbox'">
            <span class="mb-2 block">{{ fieldLabel(field) }}</span>
            <select
              v-model="draft[field.key]"
              class="h-11 w-full rounded-xl border border-n-weak bg-n-solid-1 px-3.5 text-sm text-n-slate-12 shadow-sm outline-none transition hover:border-n-slate-6 focus:border-n-brand focus:ring-2 focus:ring-n-brand/20"
            >
              <option value="">
                {{ t('INTEGRATION_SETTINGS.NATIVE.SELECT_INBOX') }}
              </option>
              <option
                v-for="inbox in integration.inboxes"
                :key="inbox.id"
                :value="String(inbox.id)"
              >
                {{ inbox.name }}
              </option>
            </select>
          </template>

          <template v-else-if="field.type === 'calendar'">
            <span class="mb-2 block">{{ fieldLabel(field) }}</span>
            <select
              v-model="draft[field.key]"
              class="h-11 w-full rounded-xl border border-n-weak bg-n-solid-1 px-3.5 text-sm text-n-slate-12 shadow-sm outline-none transition hover:border-n-slate-6 focus:border-n-brand focus:ring-2 focus:ring-n-brand/20"
            >
              <option value="">
                {{ t('INTEGRATION_SETTINGS.NATIVE.SELECT_CALENDAR') }}
              </option>
              <option
                v-for="calendar in integration.calendars"
                :key="calendar.id"
                :value="String(calendar.id)"
              >
                {{ calendar.name }}
              </option>
            </select>
          </template>

          <template v-else-if="field.type === 'pipeline'">
            <span class="mb-2 block">{{ fieldLabel(field) }}</span>
            <select
              v-model="draft[field.key]"
              class="h-11 w-full rounded-xl border border-n-weak bg-n-solid-1 px-3.5 text-sm text-n-slate-12 shadow-sm outline-none transition hover:border-n-slate-6 focus:border-n-brand focus:ring-2 focus:ring-n-brand/20"
              @change="changePipeline"
            >
              <option value="">
                {{ t('INTEGRATION_SETTINGS.NATIVE.SELECT_PIPELINE') }}
              </option>
              <option
                v-for="pipeline in integration.pipelines"
                :key="pipeline.id"
                :value="String(pipeline.id)"
              >
                {{ pipeline.name }}
              </option>
            </select>
          </template>

          <template v-else-if="field.type === 'pipeline_stage'">
            <span class="mb-2 block">{{ fieldLabel(field) }}</span>
            <select
              v-model="draft[field.key]"
              class="h-11 w-full rounded-xl border border-n-weak bg-n-solid-1 px-3.5 text-sm text-n-slate-12 shadow-sm outline-none transition hover:border-n-slate-6 focus:border-n-brand focus:ring-2 focus:ring-n-brand/20"
            >
              <option value="">
                {{ t('INTEGRATION_SETTINGS.NATIVE.SELECT_PIPELINE_STAGE') }}
              </option>
              <option
                v-for="stage in selectedPipelineStages"
                :key="stage.id"
                :value="String(stage.id)"
              >
                {{ stage.name }}
              </option>
            </select>
          </template>

          <template v-else-if="field.type === 'textarea'">
            <span class="mb-2 block font-semibold">{{
              fieldLabel(field)
            }}</span>
            <textarea
              v-model="draft[field.key]"
              :maxlength="field.maxlength"
              :rows="field.key === 'soul_closer_personality' ? 18 : 5"
              :class="
                field.key === 'soul_closer_personality'
                  ? 'min-h-[22rem] w-full'
                  : 'min-h-32 w-full'
              "
              class="resize-y rounded-xl border border-n-weak bg-n-solid-1 px-4 py-3 text-sm leading-6 text-n-slate-12 shadow-inner outline-none transition placeholder:text-n-slate-10 hover:border-n-slate-6 focus:border-n-brand focus:ring-2 focus:ring-n-brand/20"
            />
            <div
              v-if="field.key === 'soul_closer_personality'"
              class="mt-2 flex items-center justify-between gap-3 text-xs text-n-slate-10"
            >
              <span>{{ t('LUMERAI_SETTINGS.SOULCLOSER.EDITOR_HINT') }}</span>
              <span class="shrink-0 tabular-nums">
                {{ String(draft[field.key] || '').length.toLocaleString() }}
                <template v-if="field.maxlength">
                  / {{ field.maxlength.toLocaleString() }}
                </template>
              </span>
            </div>
            <span
              v-if="fieldDescription(field)"
              class="mt-2 block text-xs leading-5 text-n-slate-10"
            >
              {{ fieldDescription(field) }}
            </span>
          </template>

          <template v-else>
            <span class="mb-2 block">{{ fieldLabel(field) }}</span>
            <input
              v-model="draft[field.key]"
              :type="field.type === 'number' ? 'number' : 'text'"
              :min="field.min"
              :max="field.max"
              :step="field.step"
              class="h-11 w-full rounded-xl border border-n-weak bg-n-solid-1 px-3.5 text-sm text-n-slate-12 shadow-sm outline-none transition placeholder:text-n-slate-10 hover:border-n-slate-6 focus:border-n-brand focus:ring-2 focus:ring-n-brand/20"
            />
            <span
              v-if="fieldDescription(field)"
              class="mt-1 block text-xs text-n-slate-10"
            >
              {{ fieldDescription(field) }}
            </span>
          </template>
        </label>
      </div>
    </section>

    <footer
      class="flex flex-col gap-3 rounded-2xl border border-n-weak bg-n-solid-2 px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:px-5"
    >
      <div class="flex items-center gap-3">
        <span
          class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
          :class="
            hasChanges
              ? 'bg-n-amber-3/30 text-n-amber-11'
              : 'bg-n-teal-3/30 text-n-teal-11'
          "
        >
          <fluent-icon
            :icon="hasChanges ? 'arrow-clockwise' : 'checkmark'"
            size="18"
          />
        </span>
        <div>
          <p class="m-0 text-sm font-semibold text-n-slate-12">
            {{
              hasChanges
                ? t('LUMERAI_SETTINGS.SOULCLOSER.UNSAVED_STATE')
                : t('LUMERAI_SETTINGS.SOULCLOSER.SAVED_STATE')
            }}
          </p>
          <p class="m-0 mt-0.5 text-xs text-n-slate-10">
            {{ t('LUMERAI_SETTINGS.SOULCLOSER.SAVE_HINT') }}
          </p>
        </div>
      </div>
      <Button
        :label="t('LUMERAI_SETTINGS.SOULCLOSER.SAVE_BUTTON')"
        color="blue"
        :disabled="!hasChanges"
        :is-loading="saving"
        @click="save"
      />
    </footer>
  </div>
</template>
